import {z} from 'zod/v4';

import {DOMAIN_VALUE_BOOLEAN, DOMAIN_VALUE_PREFIX} from '../types/constants';
import {ArrayFilter, ConditionFilter, DateFilter, PropertyFilter, ValueFilter} from '../types/filter';
import {Reference, ReferenceQuery} from '../types/reference';
import {
  ArrayFilterSchema,
  ConditionFilterSchema,
  DateFilterSchema,
  PropertyFilterSchema,
  ValueFilterSchema,
} from './filter';
import {TemplateValueSchema} from './template';

const DOMAIN_VALUE_NAME_REGEX = `${DOMAIN_VALUE_PREFIX}-\\d+|${DOMAIN_VALUE_BOOLEAN}`;

export const DOMAIN_VALUE_REFERENCE_REGEX = new RegExp(`^(${DOMAIN_VALUE_NAME_REGEX})#\\w+$`);
export const DOMAIN_VALUE_LIST_REFERENCE_REGEX = new RegExp(`^(${DOMAIN_VALUE_NAME_REGEX})$`);
const STATE_VALUE_REFERENCE_REGEX = new RegExp(`^(?!${DOMAIN_VALUE_PREFIX}).+$`);

const DomainValueRefString = z.string().regex(DOMAIN_VALUE_REFERENCE_REGEX);
const DomainValueListRefString = z.string().regex(DOMAIN_VALUE_LIST_REFERENCE_REGEX);
const StateRefString = z.string().regex(STATE_VALUE_REFERENCE_REGEX);

export const SimpleReferenceSchemaFields = {
  $ref: z.union([DomainValueRefString, DomainValueListRefString, StateRefString]),
};

const BaseReferenceSchema = z.strictObject({
  ...SimpleReferenceSchemaFields,
  sort: z.enum(['asc', 'desc']).optional(),
  transform: z.enum(['count']).optional(),
  filters: z
    .lazy(
      (): z.ZodArray<z.ZodType<ValueFilter | DateFilter | PropertyFilter>> =>
        z.array(z.union([DateFilterSchema, ValueFilterSchema, PropertyFilterSchema])),
    )
    .optional(),
});

export const ReferenceSchema: z.ZodType<Reference> = z.union([
  // Case 1: No asOption field (property is optional, asOption must not be present)
  BaseReferenceSchema.extend({
    property: z.union([z.string(), TemplateValueSchema]).optional(),
    asOption: z.never().optional(),
  }),
  // Case 2: With asOption field (property is required)
  BaseReferenceSchema.extend({
    property: z.union([z.string(), TemplateValueSchema]),
    asOption: z.boolean().optional(),
  }),
]);

export const ReferenceQuerySchema: z.ZodType<ReferenceQuery> = z.strictObject({
  $query: z.string().includes('*'),
  groupBy: z.string().optional(),
  distinct: z.boolean().optional(),
  intersect: z.boolean().optional(),
  includeHiddenFields: z.boolean().optional(),
  resolve: z.boolean().optional(),
  transform: z.enum(['count']).optional(),
  sort: z.enum(['asc', 'desc']).optional(),
  property: z.union([z.string(), TemplateValueSchema]).optional(),
  filters: z
    .lazy(
      (): z.ZodArray<z.ZodType<ValueFilter | DateFilter | PropertyFilter | ArrayFilter | ConditionFilter>> =>
        z.array(
          z.union([
            DateFilterSchema,
            ValueFilterSchema,
            PropertyFilterSchema,
            ArrayFilterSchema,
            ConditionFilterSchema,
          ]),
        ),
    )
    .optional(),
});
